import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { hasAccessToSector } from "@/utils/roleHelpers";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: Array<"admin" | "gerente" | "producao" | "super_admin">;
  allowedSectors?: string[];
  requireSuperAdmin?: boolean;
  /** Com requireSuperAdmin, também libera o admin da organização (org_admin) — ex.: auditoria de Relatórios. */
  allowOrgAdmin?: boolean;
}

export default function ProtectedRoute({
  children,
  requiredRoles,
  allowedSectors,
  requireSuperAdmin,
  allowOrgAdmin,
}: ProtectedRouteProps) {
  const { user, userRole, loading, roleLoading, isRedirecting } = useAuth();

  // Mostrar loader durante carregamento OU durante redirect (evita flash)
  if (loading || isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Carregando...</span>
        </div>
      </div>
    );
  }

  // Usuário autenticado mas role ainda carregando - aguardar
  if (user && !userRole && roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Carregando permissões...</span>
        </div>
      </div>
    );
  }

  // Fallback: se tudo carregou mas userRole ainda é null, mostrar erro claro
  if (user && !userRole && !roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2 text-foreground">Erro de Permissões</h1>
          <p className="text-muted-foreground mb-4">
            Não foi possível carregar suas permissões. Tente recarregar a página.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Recarregar
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Acesso ao dono do sistema (super_admin = Walter) e, quando allowOrgAdmin, também
  // ao admin da organização (org_admin = Carlos, p/ auditoria de Relatórios). No front,
  // super_admin/org_admin são mapeados para role 'admin'; as flags isSuperAdmin/isOrgAdmin distinguem.
  if (requireSuperAdmin && !userRole?.isSuperAdmin && !(allowOrgAdmin && userRole?.isOrgAdmin)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Acesso Negado</h1>
          <p className="text-muted-foreground">
            Esta página é restrita à administração do sistema.
          </p>
        </div>
      </div>
    );
  }

  const hasRoleAccess = !requiredRoles || (userRole && requiredRoles.includes(userRole.role));
  const hasSectorAccess = !allowedSectors || allowedSectors.some(s => hasAccessToSector(userRole, s));

  // Verificar acesso por role ou setor
  if (!hasRoleAccess && !hasSectorAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Acesso Negado</h1>
          <p className="text-muted-foreground">
            Você não tem permissão para acessar esta página.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
